/**
 * Watchlist API Service (Phase 5 - Task 5.4)
 *
 * Gemini AI 추천 종목 관리 API
 */

import type {
  WatchlistData,
  RefreshResponse,
  WatchlistOverrideRequest,
  RefreshStatus,
} from '../types/watchlist';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

/**
 * 현재 Watchlist 조회
 *
 * GET /api/watchlist/current
 */
export async function getCurrentWatchlist(): Promise<WatchlistData> {
  const response = await apiFetch(`${API_BASE_URL}/api/watchlist/current`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

/**
 * Manual Refresh (수동 새로고침)
 *
 * POST /api/watchlist/refresh
 */
export async function refreshWatchlist(): Promise<RefreshResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/watchlist/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

export async function refreshPaperPreviewWatchlist(): Promise<RefreshResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/watchlist/paper-preview/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

/**
 * Watchlist 수동 덮어쓰기
 *
 * PUT /api/watchlist/override
 */
export async function overrideWatchlist(
  request: WatchlistOverrideRequest
): Promise<WatchlistData> {
  const response = await apiFetch(`${API_BASE_URL}/api/watchlist/override`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

/**
 * Refresh Status 조회 (스케줄러 상태)
 *
 * 내부 유틸리티 - 다음 새로고침 시간 계산
 */
export function getRefreshStatus(watchlistData: WatchlistData): RefreshStatus {
  const scheduledTimes = watchlistData.refresh_policy?.scheduled_slots_et || ['09:45', '13:00'];
  const now = new Date();
  const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  // 다음 새로고침 시간 계산
  let nextRefresh = '';
  for (const time of scheduledTimes) {
    const [hours, minutes] = time.split(':').map(Number);
    const scheduled = new Date(etNow);
    scheduled.setHours(hours, minutes, 0, 0);

    if (scheduled > etNow) {
      nextRefresh = time;
      break;
    }
  }

  if (!nextRefresh) {
    // 오늘 마지막 새로고침 지났으면 내일 첫 시간
    nextRefresh = scheduledTimes[0];
  }

  return {
    last_refresh: watchlistData.last_refresh || null,
    next_refresh: watchlistData.next_refresh || nextRefresh,
    refresh_count_today: watchlistData.refresh_policy?.total_calls || 0,
    scheduled_times: scheduledTimes,
  };
}

/**
 * 다음 새로고침까지 남은 시간 계산 (초) - 간격 기반
 *
 * @param lastRefresh - 마지막 새로고침 시간 (ISO 문자열)
 * @param intervalSeconds - 새로고침 간격 (초)
 * @returns 다음 새로고침까지 남은 시간 (초)
 */
export function getSecondsUntilNextRefresh(
  lastRefresh: string | null,
  intervalSeconds: number
): number {
  if (intervalSeconds <= 0) return 0;
  if (!lastRefresh) {
    // 마지막 새로고침 시간이 없으면 알 수 없음
    return 0;
  }

  const now = new Date();
  const lastRefreshTime = new Date(lastRefresh);
  const nextRefreshTime = new Date(lastRefreshTime.getTime() + intervalSeconds * 1000);

  const diff = nextRefreshTime.getTime() - now.getTime();
  return Math.max(0, Math.floor(diff / 1000));
}

export function getSecondsUntilNextSlot(nextSlotEt?: string | null): number {
  if (!nextSlotEt) return 0;
  const now = new Date();
  const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const [hours, minutes] = nextSlotEt.split(':').map(Number);
  const next = new Date(etNow);
  next.setHours(hours, minutes, 0, 0);
  if (next <= etNow) next.setDate(next.getDate() + 1);
  return Math.max(0, Math.floor((next.getTime() - etNow.getTime()) / 1000));
}
